import React from "react";
import { CheckCircle2, Filter, Search } from "lucide-react";
import { resolveEmptyCopy } from "@/lib/emptyCopy";
import useIsMobile from "@/lib/useIsMobile";
import { TID } from "@/lib/testIds";

/**
 * Why this scan came back empty — the PERSISTENT explanation, replacing the
 * toast that used to say it once and expire.
 *
 * Renders in the results area, in place of `EmptyPanel`, whenever the latest
 * scan for this lens finished with an `empty_reason`. It survives a refresh
 * because the reason lives on the scan document, not in component state — the
 * same way `ScanLoader` resumes a running scan.
 *
 * NOT the first-scan safety net. That card is correctly gated to workspaces
 * that have never had a hit, because "try widening your scope" is wrong advice
 * for a workspace whose scans normally work. This panel is what everyone else
 * gets instead of a wordless screen (Law #8 / #10).
 *
 * NOT amber, no icon-with-a-warning, no CTA on the two states whose copy says
 * there is nothing to do. An empty scan is a legitimate result.
 *
 * Props mirror the poll payload one-for-one — branch on `reason`
 * (= `empty_reason`) and NEVER on the top-level `all_seen` flag, which is also
 * true for `all_filtered`.
 */

const ICONS = {
  all_seen: CheckCircle2,
  all_filtered: Filter,
  no_matches: Search,
};

export default function EmptyScanPanel({
  reason,
  count,
  refunded,
  refundDeclined,
  refundsPerDay,
}) {
  const isMobile = useIsMobile();
  const copy = resolveEmptyCopy({
    reason,
    count,
    refunded,
    refundDeclined,
    refundsPerDay,
    isMobile,
  });

  // Unknown or absent reason: render nothing and let the caller fall back to
  // the generic empty state. We do not have an honest explanation, so we do not
  // print one.
  if (!copy) return null;

  const Icon = ICONS[reason] || Search;

  return (
    <section
      data-testid={TID.emptyScanPanel}
      data-empty-reason={reason}
      // A scan completing is a state change the user is waiting on. This is the
      // announcement the removed toast used to make — made once, by the thing
      // that stays on screen, so a screen-reader user and a sighted user are
      // told the same sentence.
      role="status"
      aria-live="polite"
      className={[
        "rounded-2xl border border-lm-border bg-lm-surface",
        "p-6 md:p-10 text-center",
        "shadow-[0_16px_40px_-24px_rgba(31,74,56,0.25)]",
        // 6px rise + fade, killed entirely under prefers-reduced-motion.
        "motion-safe:animate-[fadeIn_0.3s_ease]",
      ].join(" ")}
    >
      <div className="w-14 h-14 rounded-2xl bg-lm-tint2 flex items-center justify-center mx-auto mb-5">
        <Icon className="w-6 h-6 text-lm-primary" strokeWidth={2} aria-hidden="true" />
      </div>

      <h3
        data-testid={TID.emptyScanHeadline}
        className="text-xl md:text-2xl font-display font-semibold tracking-tight text-lm-text mb-2"
      >
        {copy.headline}
      </h3>

      {copy.support && (
        <p
          data-testid={TID.emptyScanSupport}
          className="text-lm-text2 leading-relaxed max-w-xl mx-auto"
        >
          {copy.support}
        </p>
      )}

      {copy.next && (
        <p
          data-testid={TID.emptyScanNext}
          className="text-sm text-lm-text3 leading-relaxed max-w-xl mx-auto mt-3"
        >
          {copy.next}
        </p>
      )}

      {/* One appended line, never its own card — a second card would make the
          money the headline of an event that isn't about money. */}
      {copy.money && (
        <p
          data-testid={TID.emptyScanMoney}
          data-charged={copy.charged ? "true" : "false"}
          className="text-sm text-lm-text3 max-w-xl mx-auto mt-4 pt-4 border-t border-lm-border"
        >
          {copy.money}
        </p>
      )}
    </section>
  );
}
